<template>
  <div
    ref="datepickerWrapperRef"
    v-click-outside="() => (showPicker = false)"
    :class="`datepicker-wrapper-${UID}`"
    class="relative min-h-12"
    @mouseover="flags.hover = 1"
    @mouseleave="flags.hover = 0"
  >
    <div class="w-full h-full min-h-inherit absolute top-0 left-0 z-[2]">
      <div
        class="absolute z-[2] top-[0.875rem] right-[0.6875rem] w-5 h-5 flex place-items-center justify-center"
        :class="{
          'bg-semantic-surface-surface': status === 'default' && !disabled,
        }"
      >
        <V2IconsCalendar
          :state="getCalendarType"
          width="18"
          height="20"
          :class="{
            'cursor-not-allowed': disabled,
            'cursor-pointer': !disabled,
          }"
          @click="showPicker = true"
        />
      </div>

      <V2AtomsInput
        ref="input"
        v-model="currValue"
        type="text"
        v-bind="$attrs"
        :label="label"
        :placeholder="placeholder"
        :model-type="modelType"
        :date-format="format"
        :error-message="errorMessage"
        :disabled="disabled"
        :is-success="isSuccess"
        :be-active="showPicker"
        :mobile-keyboard-type="mobileKeyboardType"
        hide-icon
        maxlength="10"
        @show-datepicker="showPicker = true"
        @blur="flags.focus = 0"
        @focus="flags.focus = 1"
      />
    </div>

    <ClientOnly>
      <date-picker
        ref="datepicker"
        v-model:value="currValue"
        v-model:open="showPicker"
        v-bind="$attrs"
        :class="`datepicker datepicker-${UID} is-${status}`"
        :type="type"
        :label="label"
        :value-type="valueType"
        :format="format"
        :disabled="disabled"
        :disabled-date="disableDate"
        :popup-class="`datepicker-popup-${UID}`"
        @pick="pickDate"
      >
        <template #icon-calendar>
          <V2IconsCalendar
            width="18"
            height="20"
          />
        </template>
      </date-picker>
    </ClientOnly>
  </div>
</template>

<script setup>
import dayjs from 'dayjs'
import 'vue-datepicker-next/index.css'

defineOptions({
  name: 'V2AtomsDatepicker',
  inheritAttrs: false
})

const props = defineProps({
  type: {
    type: String,
    default: 'date',
    validator: value =>
      ['date', 'datetime', 'year', 'month', 'time', 'week'].includes(value)
  },

  format: {
    type: String,
    default: 'DD/MM/YYYY'
  },

  valueType: {
    type: String,
    default: 'DD/MM/YYYY'
  },

  modelType: {
    value: String,
    default: 'text',
    validator: value => ['text', 'date'].includes(value)
  },

  label: {
    type: String,
    default: ''
  },

  placeholder: {
    type: String,
    default: ''
  },

  errorMessage: {
    type: String,
    default: ''
  },

  isSuccess: {
    type: Boolean,
    default: false
  },

  // This will disable the date selection in date picker
  disableDate: {
    type: Function,
    default: () => false
  },

  mobileKeyboardType: {
    type: String,
    default: 'text'
  }
})

const emit = defineEmits([
  'input'
])

const UID = useId()
const $attrs = useAttrs()

const datepickerWrapperRef = ref()

const currValue = defineModel({ type: String, default: '' })

const showPicker = ref(false)

const flags = reactive({
  focus: 0,
  hover: 0
})

const datepicker = ref(null)

const disabled = computed(() => {
  return (
    $attrs?.disabled === '' ||
    $attrs?.disabled ||
    $attrs?.['aria-disabled'] === '' ||
    $attrs?.['aria-disabled']
  )
})

const status = computed(() => {
  const invalidDate =
    currValue && `${datepicker.value?.currentValue}` === 'Invalid Date'

  if (
    props.valueType === 'mm/yyyy' &&
    currValue.value !== null &&
    currValue.value.length === 7
  ) {
    const val = currValue.value.split('/')
    const currDate = dayjs().format('MM/YYYY').split('/')

    if (
      val.length === 2 &&
      ((val[0] >= currDate[0] && currDate[1] === val[1]) ||
        val[1] > currDate[1]) &&
      props.isSuccess
    ) {
      return 'success'
    }
  }

  if (props.disabled) {
    return 'disabled'
  } 
  
  if (flags.hover) {
    return 'hovered'
  } 
  
  if (flags.focus) {
    return 'focused'
  } 
  
  if (props.errorMessage || invalidDate) {
    return 'error'
  } 
  
  if (props.isSuccess || currValue.value) {
    return 'success'
  }

  return 'default'
})

const datepickerPopupEl = computed(() => (import.meta && document.querySelector(`.datepicker-popup-${UID}`)) || null)

watch(() => currValue.value, val => {
  emit('input', val)
})

const getCalendarType = computed(() => {
  if (status.value === 'error' || props.errorMessage) {
    return 'error'
  } else if (
    ['hovered', 'focused'].includes(status.value) ||
    showPicker.value
  ) {
    return 'active'
  }
  return 'default'
})

onMounted(async () => {
  await nextTick()

  const datepickerEl = datepicker.value

  if (datepickerEl) {
    datepickerEl
      .getElementsByTagName('input')[0]
      .setAttribute('tabindex', '-1')
  }
})

function pickDate (date) {
  currValue.value = dayjs(date).format(props.format)
}

defineExpose({
  status,
  currValue,
  $el: datepickerWrapperRef,
  datepicker,
  UID,
  datepickerPopupClass: `datepicker-popup-${UID}`,
  datepickerPopupEl
})
</script>
